// ChatPaneV2 (issue #420). Hosts the existing ChatContent + BottomBarContent
// components, but drives them with the new v2 hooks (`useChatMessages`,
// `useSendMessage`, `useMarkRead`) instead of the legacy fetch logic.
//
// We deliberately do not delete or rewrite `ChatContent.tsx` and
// `BottomBarContent.tsx` — they still own the message rendering, attachment
// upload, and pin UX. This wrapper threads in messages from the cursor-
// paginated infinite query and stubs the legacy `socket.emit('chat:join')`
// the inner ChatContent issues on mount.
//
// The event router (#418) handles incoming `chat:message:new` events for
// the whole app, so this pane does not subscribe to the socket itself.

import { Box, Button, CircularProgress } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';

import BottomBarContent from './BottomBarContent';
import ChatContent from './ChatContent';
import SupportThreadComposer from './SupportThreadComposer';
import { isAdminRole, readSession } from './messengerSession';

import { useChatMessages, useMarkRead } from 'src/queries/chat';
import { useChatSocket } from 'src/realtime/ChatSocketProvider';

import type { Chat, ChatMessage } from 'src/models/chat';

interface ChatPaneV2Props {
  supportScope?: 'legacy' | 'platform';
  chat: Chat;
  /**
   * Hide the message composer (e.g. for `JOB_BROADCAST` rooms where the
   * caller cannot reply). The legacy BottomBarContent always renders, so
   * we conditionally swap it out.
   */
  readOnly?: boolean;
}

/**
 * Flatten the infinite-query pages into a chronological list (oldest first)
 * the way the legacy `ChatContent` expects. Page 0 is newest, so we reverse
 * each page's `data` (which is also newest-first) before concatenating in
 * reverse page order.
 */
function flattenMessages(
  pages: Array<{ data: ChatMessage[] }> | undefined
): ChatMessage[] {
  if (!pages || pages.length === 0) return [];
  const ascending: ChatMessage[] = [];
  for (let i = pages.length - 1; i >= 0; i--) {
    const reversed = [...pages[i].data].reverse();
    ascending.push(...reversed);
  }
  return ascending;
}

export default function ChatPaneV2({ chat, readOnly, supportScope = 'legacy' }: ChatPaneV2Props) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { socket } = useChatSocket();
  const markRead = useMarkRead(supportScope);

  // Admin-only internal-note composer is rendered for SUPPORT threads. The
  // session role gate mirrors the SupportInbox guard — the backend remains
  // the authoritative gate; this just avoids baiting a non-admin into a 403.
  const session = useMemo(() => readSession(), []);
  const showSupportComposer =
    chat.type === 'SUPPORT' && isAdminRole(session?.roleName);

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatMessages(chat.id, undefined, supportScope);
  const messages = useMemo(() => flattenMessages(data?.pages), [data?.pages]);

  // Mark the latest message as read on mount of a chat. Debounce avoids a
  // tight loop when a flurry of socket pushes lands in quick succession.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    const handle = window.setTimeout(() => {
      markRead.mutate({ chatId: chat.id, messageId: last.id });
    }, 1000);
    return () => window.clearTimeout(handle);
    // Intentionally subscribed only to `chat.id` and `messages.length` to
    // avoid retriggering on every cache mutation; `markRead` is stable
    // across renders inside react-query.
  }, [chat.id, messages.length]);

  const scrollToBottom = () => {
    const node = messagesEndRef.current;
    if (node) node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  };

  // The legacy ChatContent expects an `setMessages` setter so it can append
  // socket messages locally. With the v2 router that update flows through
  // the React Query cache, so we hand it a no-op setter and rely on
  // `messages` re-rendering when the cache changes.
  const noopSetMessages = (
    _next:
      | ChatMessage[]
      | ((prev: ChatMessage[]) => ChatMessage[])
  ): void => {
    /* no-op: the v2 cache is the source of truth */
  };

  if (isFetching && messages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box
        flex={1}
        sx={{ height: '100%', overflow: 'auto', scrollbarWidth: 'thin' }}
        ref={messagesEndRef}
      >
        {hasNextPage && (
          <Box display="flex" justifyContent="center" p={2}>
            <Button
              variant="contained"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'View previous messages'}
            </Button>
          </Box>
        )}
        {/* Retain message rendering; the cursor query is the sole history transport. */}
        {socket && (
          <ChatContent
            chat={chat}
            messages={messages}
            setMessages={noopSetMessages}
            scrollToBottom={scrollToBottom}
            socket={socket}
            supportScope={supportScope}
            managedMessages
          />
        )}
      </Box>
      {!readOnly && socket && showSupportComposer && (
        <SupportThreadComposer
          chat={chat}
          messages={messages}
          setMessages={noopSetMessages}
          scrollToBottom={scrollToBottom}
          socket={socket}
          supportScope={supportScope}
          canonicalTransport
        />
      )}
      {!readOnly && socket && !showSupportComposer && (
        <BottomBarContent
          chat={chat}
          messages={messages}
          setMessages={noopSetMessages}
          scrollToBottom={scrollToBottom}
          socket={socket}
          supportScope={supportScope}
          canonicalTransport
        />
      )}
    </>
  );
}
